import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart3, Flame } from 'lucide-react';
import { dashboardService } from '@/app/services/dashboardService';
import { cn } from '@/lib/utils';

interface TaskCompletionCountersWidgetProps {
  className?: string;
}

const TaskCompletionCountersWidget: React.FC<TaskCompletionCountersWidgetProps> = ({
  className,
}) => {
  const [period, setPeriod] = useState<'daily' | 'weekly'>('daily');

  const { data, isLoading } = useQuery({
    queryKey: ['task-completion-stats', period],
    queryFn: () => dashboardService.getTaskCompletionStats(period),
    refetchInterval: 30000,
  });

  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  if (isLoading) {
    return (
      <Card className={cn("h-full", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-7 w-28 rounded-md" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-12 w-full rounded-md mb-4" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-7 w-7 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-1 w-full" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const teamProgress = data?.team_target
    ? Math.round((data.team_total_completed / data.team_target) * 100)
    : 0;

  return (
    <Card className={cn("h-full", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5" />
            Team Progress
          </CardTitle>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as 'daily' | 'weekly')}>
            <TabsList className="h-7">
              <TabsTrigger value="daily" className="text-xs px-2.5 py-0.5 h-5">
                Today
              </TabsTrigger>
              <TabsTrigger value="weekly" className="text-xs px-2.5 py-0.5 h-5">
                Week
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Team Total Progress */}
        <div className="rounded-md bg-muted/40 p-3">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm font-medium tabular-nums">
              {data?.team_total_completed || 0}
              <span className="text-muted-foreground font-normal">
                {' '}/ {data?.team_target || 0}
              </span>
            </span>
            <span className="text-xs font-medium tabular-nums text-muted-foreground">
              {teamProgress}%
            </span>
          </div>
          <Progress value={teamProgress} className="h-1.5" />
        </div>

        {/* Individual Progress */}
        <div className="space-y-3 max-h-[200px] overflow-y-auto">
          {data?.stats && data.stats.length > 0 ? (
            data.stats.slice(0, 5).map((user, index) => {
              const percentage = user.total_assigned > 0
                ? Math.round((user.completed_count / user.total_assigned) * 100)
                : 0;
              const isTopPerformer = index === 0 && user.completed_count > 0;

              return (
                <div key={user.user_id} className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback
                        className={cn(
                          "text-xs font-medium",
                          isTopPerformer
                            ? "bg-warning/10 text-warning dark:bg-warning/40"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {getInitials(user.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    {isTopPerformer && (
                      <Flame className="absolute -top-1 -right-1 h-3 w-3 text-warning" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm truncate">{user.user_name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums ml-2">
                        {user.completed_count}/{user.total_assigned}
                      </span>
                    </div>
                    <Progress value={percentage} className="h-1" />
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No activity {period === 'daily' ? 'today' : 'this week'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskCompletionCountersWidget;
